import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AddToList from '../components/AddToList.jsx'
import Artwork from '../components/Artwork.jsx'
import BlindControl from '../components/BlindControl.jsx'
import Histogram from '../components/Histogram.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import RatingSheet from '../components/RatingSheet.jsx'
import ReviewCard from '../components/ReviewCard.jsx'
import RevisionTimeline from '../components/RevisionTimeline.jsx'
import ReviewComposer from '../components/ReviewComposer.jsx'
import ReportDialog from '../components/ReportDialog.jsx'
import ScorePill from '../components/ScorePill.jsx'
import { api, ApiError, is404 } from '../lib/api.js'
import { duration, plural } from '../lib/format.js'
import { METHOD_LABELS, STATE_LABELS, coverageLabel, display } from '../lib/score.js'

/**
 * The album page (PLAN §7).
 *
 * Two requests, not one. `api.album` is the catalog — identical for everybody
 * and cacheable — and `api.albumRating` is what this person thinks, plus what
 * everybody else does. They render independently, so the page appears as soon
 * as the album is known rather than waiting on the slower half.
 *
 * Every rating write returns the whole block, so the score, the coverage line
 * and the community histogram move together and the page never shows a state
 * that was not true (DEC-2026-08-01-004 makes those three interdependent).
 */
// How many bonus tracks to show before asking. A 2009 reissue of OK Computer
// carries twenty-one of them, which is more page than the album itself.
const BONUS_PREVIEW = 8

export default function Album() {
  const { id } = useParams()
  const [album, setAlbum] = useState(null)
  const [rating, setRating] = useState(null)
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [busy, setBusy] = useState(false)
  const [sheetError, setSheetError] = useState(null)
  const [showAllBonus, setShowAllBonus] = useState(false)
  const [reviews, setReviews] = useState(null)
  const [composing, setComposing] = useState(false)
  const [blindBusy, setBlindBusy] = useState(false)
  // One sentence per card, shared by that card's three actions — the same
  // arrangement ReviewDetail's `actionError` has, one page of cards down.
  const [cardErrors, setCardErrors] = useState({})
  // The cards this person has reported here, so the card can say so and take
  // its own *Report* button away, as the review's own page does.
  const [reported, setReported] = useState(() => new Set())
  // The card whose *Report* is asking why (Q28), and whether its send is out.
  const [reporting, setReporting] = useState(null)
  const [reportBusy, setReportBusy] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setAlbum(null)
    setRating(null)
    setError(null)
    api
      .album(id, { signal: controller.signal })
      .then(setAlbum)
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err)
      })
    api
      .albumRating(id, { signal: controller.signal })
      .then(setRating)
      .catch(() => {
        /* The album is still worth reading without its scores. */
      })
    // A third request, for the same reason the second one exists: the catalog
    // half of this page is identical for everybody and cacheable, and the two
    // personal halves are neither.
    api
      .albumReviews(id, { signal: controller.signal })
      .then(setReviews)
      .catch(() => {})
    return () => controller.abort()
  }, [id])

  // `error` is in the dependency list because an in-app link to a bad id keeps
  // this component mounted: `setAlbum(null)` runs and an effect keyed on
  // `[album]` alone never fires, so the tab would keep the previous album's
  // title.
  useEffect(() => {
    if (album) document.title = `${album.title} — Sonologue`
    else if (is404(error)) document.title = 'Not found — Sonologue'
  }, [album, error])

  /** The per-release toggle. Answers with the whole block, so one call. */
  const setBlind = useCallback(
    async (blind) => {
      setBlindBusy(true)
      try {
        setRating(await api.setAlbumBlind(id, blind))
      } catch {
        // Deliberately quiet. Failing to change a preference is not worth an
        // alert on a page whose job is rating an album; the control simply
        // stays where it was, which is the truth.
      } finally {
        setBlindBusy(false)
      }
    },
    [id],
  )

  function setCardError(publicId, message) {
    setCardErrors((current) => ({ ...current, [publicId]: message }))
  }

  async function reactToReview(target) {
    setCardError(target.public_id, '')
    try {
      const result = await api.react('review', target.public_id)
      setReviews((current) => ({
        ...current,
        items: current.items.map((item) =>
          item.public_id === target.public_id
            ? { ...item, reacted: result.reacted, reaction_count: result.count }
            : item,
        ),
      }))
    } catch (err) {
      setCardError(
        target.public_id,
        err instanceof ApiError ? err.message : 'Could not save that just now.',
      )
    }
  }

  async function removeReview(target) {
    setCardError(target.public_id, '')
    try {
      await api.deleteReview(target.public_id)
      // Locally first, then reconcile. A person has one review per album, so
      // deleting it takes the draft the composer reads with it.
      setReviews((current) => ({
        ...current,
        items: current.items.filter((item) => item.public_id !== target.public_id),
        mine: null,
      }))
      try {
        setReviews(await api.albumReviews(id))
      } catch {
        // The only silence in this file's handlers, and it is on a read that
        // follows a write that landed: the page already holds the truth, and
        // "That did not delete." about a delete that worked would be a lie.
      }
    } catch (err) {
      setCardError(target.public_id, err instanceof ApiError ? err.message : 'That did not delete.')
    }
  }

  async function sendReport(reason) {
    const target = reporting
    setReportBusy(true)
    setCardError(target.public_id, '')
    try {
      await api.report({
        target_type: 'review',
        target_public_id: target.public_id,
        reason,
      })
      setReported((current) => new Set(current).add(target.public_id))
    } catch (err) {
      // The card stays unreported, so *Report* is still offered and the
      // "Reported." line never claims something that did not happen.
      setCardError(
        target.public_id,
        err instanceof ApiError ? err.message : 'That report did not go through.',
      )
    } finally {
      setReportBusy(false)
      setReporting(null)
    }
  }

  const save = useCallback(
    async (call) => {
      setBusy(true)
      setSheetError(null)
      try {
        setRating(await call())
        setSheet(null)
      } catch (err) {
        setSheetError(err instanceof ApiError ? err.message : 'Could not save that just now.')
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  if (error) {
    return (
      <div className="card">
        <div className="empty-state">
          <h1>{is404(error) ? 'No such album' : 'Not loaded'}</h1>
          <p>
            {error instanceof ApiError
              ? error.message
              : 'Sonologue could not load this album just now.'}
          </p>
          {is404(error) ? null : (
            <button
              type="button"
              className="btn btn-tonal"
              onClick={() => window.location.reload()}
            >
              Try again
            </button>
          )}
          <Link className="btn btn-quiet" to="/search">
            Back to search
          </Link>
        </div>
      </div>
    )
  }

  if (!album) {
    return (
      <div className="album-hero">
        <div className="skeleton album-cover" style={{ aspectRatio: '1 / 1' }} />
        <div className="album-meta">
          <div className="skeleton" style={{ height: 20, width: 140 }} />
          <div className="skeleton" style={{ height: 38, width: 260 }} />
        </div>
      </div>
    )
  }

  const facts = [
    album.primary_type,
    ...(album.secondary_types || []),
    album.year ? String(album.year) : null,
    album.tracks.length ? `${album.tracks.length} ${plural(album.tracks.length, 'track')}` : null,
  ].filter(Boolean)

  const mine = rating?.mine || null
  const average = rating?.track_average || null
  const community = rating?.community || null
  const trackScores = new Map((rating?.tracks || []).map((t) => [t.song_public_id, t]))
  const subtitle = [album.credit_name, album.year].filter(Boolean).join(' · ')
  const edits = mine ? Math.max(0, (mine.revision_count || 1) - 1) : 0

  function openTrack(track) {
    setSheetError(null)
    setSheet({ kind: 'track', track })
  }

  return (
    <>
      <div className="album-hero fade-up">
        <div className="album-cover">
          <Artwork
            art={album.artwork}
            title={album.title}
            artist={album.credit_name}
            size={280}
            rounded={false}
            className="art-fill"
            priority
          />
        </div>
        <div className="album-meta">
          <div className="album-artist">
            {album.credits.length ? (
              album.credits.map((credit) => (
                <span key={credit.public_id}>
                  <Link to={`/artist/${credit.public_id}`}>{credit.name}</Link>
                  {credit.join_phrase}
                </span>
              ))
            ) : (
              <span>{album.credit_name}</span>
            )}
          </div>
          <h1 className="large-title">{album.title}</h1>
          {album.disambiguation ? <div className="footnote">{album.disambiguation}</div> : null}
          <div className="album-facts">{facts.join(' · ')}</div>

          {album.genres.length ? (
            <div className="genre-row">
              {album.genres.map((genre) => (
                <span className="state-chip" key={genre}>
                  {genre}
                </span>
              ))}
            </div>
          ) : null}

          <div className="album-cta">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setSheetError(null)
                setSheet({ kind: 'album' })
              }}
            >
              {mine ? `Your rating · ${display(mine.score)}` : 'Rate this album'}
            </button>
            {mine?.visibility === 'private' ? (
              <span className="state-chip">private</span>
            ) : null}
          </div>

          {/* Its own row, under the score and never inside `.album-cta`: that
              row wraps, so a second button there sits beside the rating
              button, and the empty list page says this one is under it. */}
          <AddToList
            targetType="release_group"
            targetPublicId={album.public_id}
            subject={[album.title, album.credit_name].filter(Boolean).join(' · ')}
          />

          <div className="deep-links" aria-label="Listen on external services">
            {album.deep_links.map((link) => (
              <a
                key={link.provider}
                className="deep-link"
                href={link.href}
                target="_blank"
                rel="noreferrer noopener"
              >
                Listen on {link.label} ↗
              </a>
            ))}
          </div>
        </div>
      </div>

      {rating ? (
        <div className="stat-grid fade-up">
          <div className="card stat-cell">
            <div className="stat-value num">
              {community?.blind ? '·' : (display(community?.mean) ?? '—')}
            </div>
            <div className="stat-label">
              {community?.blind
                ? `Hidden · ${community.count} rating${community.count === 1 ? '' : 's'} so far`
                : community?.count
                  ? `Community · ${community.count} rating${community.count === 1 ? '' : 's'}`
                  : 'Community · no ratings yet'}
            </div>
          </div>
          {mine ? (
            <div className="card stat-cell">
              <div className="stat-value num mine">{display(mine.score)}</div>
              <div className="stat-label">
                You · {METHOD_LABELS[mine.method]}
                {edits ? ` · edited ${edits} time${edits === 1 ? '' : 's'}` : ''}
              </div>
              {mine.state ? (
                <span className="state-chip">{STATE_LABELS[mine.state]}</span>
              ) : null}
            </div>
          ) : null}
          <div className="card stat-cell">
            <div className="stat-value num">
              {average?.available ? display(average.value) : '—'}
            </div>
            <div className="stat-label">Your track average · {coverageLabel(average)}</div>
          </div>
        </div>
      ) : null}

      {mine ? (
        <div className="card card-pad fade-up timeline-card">
          <RevisionTimeline kind="album" id={id} revisionCount={mine.revision_count} />
        </div>
      ) : null}

      <div className="section-header">
        <h2 className="title-2">Rating distribution</h2>
        {community?.blind ? null : community?.count ? (
          <span className="footnote">{community.count} scored this album</span>
        ) : null}
      </div>
      <div className="card card-pad fade-up">
        {community?.blind ? (
          /* Not a zeroed chart. A histogram of nothing looks like an album
             nobody has heard, which is the opposite of what is true here. */
          <p className="footnote mb-0">
            Hidden while blind mode is on. Rate this album and the distribution appears.
          </p>
        ) : (
          <Histogram
            data={community?.histogram || []}
            count={community?.count || 0}
            mean={community?.mean ?? null}
            bucketWidth={community?.bucket_width}
          />
        )}
        {rating?.blind ? (
          <p className="footnote">Rate it to see what everybody else thought.</p>
        ) : null}
        <BlindControl
          blind={Boolean(rating?.blind)}
          override={rating?.blind_override}
          busy={blindBusy}
          onSet={setBlind}
        />
      </div>

      <div className="section-header">
        <h2 className="title-2">Tracks</h2>
        <span className="footnote">Community · yours</span>
      </div>

      {album.tracklist_pending ? (
        <div className="card">
          <div className="empty-state">
            <h2>No tracklist yet</h2>
            <p>
              Sonologue knows this album but has not fetched its tracks. That happens on the first
              visit — reload once MusicBrainz is answering again.
            </p>
          </div>
        </div>
      ) : (
        <div className="card hairline-list fade-up">
          {album.tracks.map((track) => (
            <TrackRow
              key={`${track.position}-${track.song_public_id}`}
              track={track}
              scores={trackScores.get(track.song_public_id)}
              onRate={() => openTrack(track)}
            />
          ))}
        </div>
      )}

      {album.bonus_tracks?.length ? (
        <>
          <div className="section-header">
            <h2 className="title-2">Bonus and deluxe tracks</h2>
            <span className="footnote">
              {average?.include_bonus ? 'Counted in your average' : 'Not counted in your average'}
            </span>
          </div>
          <div className="card hairline-list">
            {(showAllBonus ? album.bonus_tracks : album.bonus_tracks.slice(0, BONUS_PREVIEW)).map(
              (track) => (
                <TrackRow
                  key={`bonus-${track.song_public_id}`}
                  track={track}
                  scores={trackScores.get(track.song_public_id)}
                  onRate={() => openTrack(track)}
                />
              ),
            )}
          </div>
          {album.bonus_tracks.length > BONUS_PREVIEW ? (
            <p className="mt-4">
              <button
                type="button"
                className="link-button"
                onClick={() => setShowAllBonus((open) => !open)}
              >
                {showAllBonus
                  ? 'Show fewer'
                  : `Show all ${album.bonus_tracks.length} bonus tracks`}
              </button>
            </p>
          ) : null}
          <p className="caption mt-4">
            Whether these count towards your track average is yours to choose, in{' '}
            <Link className="link-button" to="/settings">
              settings
            </Link>
            .
          </p>
        </>
      ) : null}

      <div className="section-header">
        <h2 className="title-2">Reviews</h2>
        <button type="button" className="btn btn-tonal btn-sm" onClick={() => setComposing(true)}>
          {reviews?.mine ? 'Edit your review' : 'Write a review'}
        </button>
      </div>

      {reviews === null ? (
        <div className="card card-pad">
          <p className="footnote mb-0">Loading reviews…</p>
        </div>
      ) : reviews.items.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h2>No reviews yet</h2>
            <p>
              {reviews.mine
                ? 'Yours is still a draft. Publish it and it lands here.'
                : 'Be the first to say something about this record.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="review-stack">
          {reviews.items.map((review) => (
            <ReviewCard
              key={review.public_id}
              review={review}
              onEdit={() => setComposing(true)}
              onReact={reactToReview}
              onDelete={removeReview}
              onReport={reported.has(review.public_id) ? undefined : setReporting}
            >
              <InlineAlert>{cardErrors[review.public_id]}</InlineAlert>
              {reported.has(review.public_id) ? (
                <p className="footnote" role="status">
                  Reported. The operator will look at it.
                </p>
              ) : null}
              <Link to={`/reviews/${review.public_id}`} className="footnote review-more">
                Open the thread
              </Link>
            </ReviewCard>
          ))}
        </div>
      )}


      {album.editions.length > 1 ? (
        <>
          <div className="section-header">
            <h2 className="title-2">Editions</h2>
            <span className="footnote">One album page, every pressing</span>
          </div>
          <div className="card hairline-list">
            {album.editions.map((edition) => (
              <div className="row" key={edition.public_id}>
                <div className="row-main">
                  <div className="row-title">
                    {edition.title}
                    {edition.edition_note ? ` — ${edition.edition_note}` : ''}
                  </div>
                  <div className="row-sub">
                    {[
                      edition.date_text,
                      edition.country,
                      edition.media_format,
                      edition.track_count
                        ? `${edition.track_count} ${plural(edition.track_count, 'track')}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                {edition.is_primary ? <span className="state-chip">shown above</span> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {album.artwork.attribution ? (
        <p className="caption mt-4">{album.artwork.attribution}</p>
      ) : null}
      <p className="caption">Metadata from MusicBrainz.</p>

      {sheet?.kind === 'album' ? (
        <RatingSheet
          title={album.title}
          subtitle={subtitle}
          artwork={album.artwork}
          variant="album"
          initialScore={mine?.direct_score ?? mine?.score ?? null}
          initialMethod={mine?.method || 'direct'}
          initialState={mine?.state || null}
          initialVisibility={mine?.visibility || 'public'}
          initialSubscores={mine?.subscores || null}
          initialSubscoreMode={mine?.subscore_mode || 'independent'}
          initialSubscoreWeights={mine?.subscore_weights || null}
          initialTags={mine?.tags || null}
          trackAverage={average}
          canRemove={Boolean(mine)}
          busy={busy}
          error={sheetError}
          onSave={(payload) => save(() => api.rateAlbum(id, payload))}
          onRemove={() => save(() => api.unrateAlbum(id))}
          // Not routed through `save`: a suggestion changes no rating, so it
          // must not put the sheet into its saving state or refresh the block.
          onSuggestTag={(text) => api.suggestAlbumTag(id, text)}
          onClose={() => setSheet(null)}
        />
      ) : null}

      <ReportDialog
        open={Boolean(reporting)}
        busy={reportBusy}
        onSend={sendReport}
        onClose={() => setReporting(null)}
      />

      <ReviewComposer
        open={composing}
        review={reviews?.mine}
        subject={`${album.title} · ${album.credit_name}`}
        onClose={() => setComposing(false)}
        onSave={async (payload) => setReviews(await api.saveAlbumReview(id, payload))}
      />

      {sheet?.kind === 'track' ? (
        <RatingSheet
          title={sheet.track.title}
          subtitle={`${album.title} · ${album.credit_name}`}
          variant="track"
          initialScore={trackScores.get(sheet.track.song_public_id)?.mine ?? null}
          canRemove={trackScores.get(sheet.track.song_public_id)?.mine != null}
          busy={busy}
          error={sheetError}
          onSave={(payload) =>
            save(() => api.rateTrack(id, sheet.track.song_public_id, payload))
          }
          onRemove={() => save(() => api.unrateTrack(id, sheet.track.song_public_id))}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </>
  )
}

/**
 * A track row: the title links to the song, the score opens the sheet.
 *
 * Not one big link with a button inside it — that is invalid HTML and a
 * screen reader reads it as one confused control. Two targets, each saying
 * what it does.
 */
function TrackRow({ track, scores, onRate }) {
  const mine = scores?.mine ?? null
  // A bonus row may name its recording (Q27): three takes of one song on a
  // reissue otherwise read identically, here and to a screen reader.
  const name = track.recording_title || track.title
  return (
    <div className="row track-row">
      <span className="track-index" aria-hidden={track.is_bonus || undefined}>
        {track.is_bonus ? '·' : track.number || track.position}
      </span>
      <div className="row-main">
        <Link className="track-name" to={`/song/${track.song_public_id}`}>
          {track.title}
        </Link>
        {track.recording_title ? <p className="footnote mb-0">{track.recording_title}</p> : null}
      </div>
      {track.is_bonus ? <span className="state-chip">bonus</span> : null}
      <span className="track-duration">{duration(track.length_ms) || '—'}</span>
      <ScorePill
        score={scores?.community_mean ?? null}
        label={
          scores?.community_count
            ? `Community score for ${name}`
            : `No community score for ${name} yet`
        }
      />
      <button
        type="button"
        className="track-rate"
        onClick={onRate}
        aria-label={
          mine == null
            ? `Rate ${name}`
            : `Your score for ${name} is ${display(mine)} — change it`
        }
      >
        <ScorePill score={mine} mine />
      </button>
    </div>
  )
}
